import { useEffect, useMemo, useRef, useState } from 'react';
import type { PanelDesign, WallMesh } from '@/core/types';
import { buildWallMesh, wallMeshStats, type MeshStats } from '@/core/mesh/panelMesh';
import { clamp } from '@/core/math/scalar';

/**
 * Live mesh generation for the viewport.
 *
 * Two things keep a configurator usable while sliders are being dragged:
 * debouncing, so a drag produces one rebuild rather than sixty, and a triangle
 * budget, so a 12x12 wall previews at a resolution the browser can actually
 * carry. The export path ignores both and always uses the design's own
 * resolution.
 */

const PREVIEW_TRIANGLE_BUDGET = 600_000;

/** Highest per-panel resolution that keeps the whole wall inside the budget. */
export function previewSegments(design: PanelDesign): number {
  const panels = Math.max(1, design.grid.columns * design.grid.rows);
  const perPanel = PREVIEW_TRIANGLE_BUDGET / panels;
  const segments = Math.floor(Math.sqrt(perPanel / 2) / 4) * 4;
  return clamp(segments, 16, design.panel.segments);
}

interface GeneratedWall {
  /** The design this result was built from, used to tell whether it is stale. */
  source: PanelDesign | null;
  wall: WallMesh | null;
  stats: MeshStats | null;
  error: string | null;
  segments: number;
}

export interface WallMeshResult extends Omit<GeneratedWall, 'source'> {
  /** True while the displayed mesh does not yet match the current design. */
  building: boolean;
}

export function useWallMesh(design: PanelDesign, delay = 140): WallMeshResult {
  const [generated, setGenerated] = useState<GeneratedWall>({
    source: null,
    wall: null,
    stats: null,
    error: null,
    segments: 0,
  });

  const segments = useMemo(() => previewSegments(design), [design]);
  const latest = useRef(0);

  useEffect(() => {
    const generation = ++latest.current;

    const timer = setTimeout(() => {
      // Yield a frame first so the pending state paints before the main thread
      // is occupied by generation.
      requestAnimationFrame(() => {
        if (latest.current !== generation) return;
        try {
          const wall = buildWallMesh(design, { segments });
          if (latest.current !== generation) return;
          setGenerated({
            source: design,
            wall,
            stats: wallMeshStats(wall),
            error: null,
            segments,
          });
        } catch (error) {
          if (latest.current !== generation) return;
          setGenerated({
            source: design,
            wall: null,
            stats: null,
            error: error instanceof Error ? error.message : String(error),
            segments,
          });
        }
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [design, segments, delay]);

  // Derived rather than stored: a result built from a different design object
  // is by definition stale, so no extra render is needed to say so.
  return {
    wall: generated.wall,
    stats: generated.stats,
    error: generated.error,
    segments: generated.segments,
    building: generated.source !== design,
  };
}
